import { getCSRFToken } from '@cartwheel/api'
import * as React from 'react'

// Types
interface PayrollProps {
  mode: 'form' | 'progress'
  // Form props
  previousPayPeriod?: {
    id?: number
    start_date: string
    end_date: string
    created_at?: string
  }
  nextPayPeriod?: {
    start_date: string
    end_date: string
  }
  calculatedBonusPeriod?: boolean
  payableClinicianCount?: number
  currentUserEmail?: string
  // Progress props
  payPeriodId?: number
  // Shared paths
  payrollPath: string
  payrollProgressPath?: string
  payrollFailuresPath?: string
  payPeriodPath?: string
  previousPayPeriodPath?: string
}

interface ProgressData {
  total: number
  completed: number
  failed: number
  pending: number
  percentage: number
  is_complete: boolean
  finalized: boolean
}

interface FailureData {
  job_id: string
  clinician_id: number
  error: string
  failed_at: number
}

// Progress Component
function PayrollProgress({
  payrollProgressPath,
  payrollFailuresPath,
  payPeriodPath,
}: {
  payrollProgressPath: string
  payrollFailuresPath: string
  payPeriodPath?: string
}) {
  const [progress, setProgress] = React.useState<ProgressData | null>(null)
  const [failures, setFailures] = React.useState<FailureData[]>([])
  const [showFailures, setShowFailures] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null)

  const fetchFailures = React.useCallback(async () => {
    try {
      const response = await fetch(payrollFailuresPath, {
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) throw new Error('Failed to fetch failures')

      const data = await response.json()
      setFailures(data.failures || [])
    } catch (err) {
      console.error('Failed to load failures', err)
    }
  }, [payrollFailuresPath])

  const fetchProgress = React.useCallback(async () => {
    try {
      const response = await fetch(payrollProgressPath, {
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) throw new Error('Failed to fetch progress')

      const data: ProgressData = await response.json()
      setProgress(data)

      // If complete or finalized, stop polling
      if (data.is_complete || data.finalized) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }

      // If there are failures, fetch failure details
      if (data.failed > 0) {
        void fetchFailures()
      }
    } catch (err) {
      setError('Failed to load progress')
      console.error(err)
    }
  }, [payrollProgressPath, fetchFailures])

  React.useEffect(() => {
    void fetchProgress()
    intervalRef.current = setInterval(fetchProgress, 2000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [fetchProgress])

  const getStatusColor = () => {
    if (!progress) return 'bg-gray-200'
    if (progress.failed > 0) return 'bg-yellow-500'
    if (progress.is_complete) return 'bg-green-500'
    return 'bg-blue-500'
  }

  const getStatusText = () => {
    if (!progress) return 'Loading...'
    if (progress.finalized) return '✅ Complete - Reports Sent'
    if (progress.is_complete) return 'Processing complete - Sending reports...'
    if (progress.percentage === 100) return 'Finalizing...'
    return `Processing: ${progress.completed} of ${progress.total} clinicians`
  }

  if (error) {
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-800">{error}</p>
      </div>
    )
  }

  return (
    <>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Payroll Run Progress</h1>

      {progress && (
        <>
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{getStatusText()}</span>
              <span className="text-sm font-medium text-gray-700">{progress.percentage}%</span>
            </div>
            <div className="w-full h-4 bg-gray-200 rounded-full">
              <div
                className={`h-4 rounded-full transition-all duration-500 ${getStatusColor()}`}
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 text-center rounded-lg bg-gray-50">
              <div className="text-2xl font-bold text-gray-900">{progress.total}</div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
            <div className="p-4 text-center rounded-lg bg-green-50">
              <div className="text-2xl font-bold text-green-700">{progress.completed}</div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="p-4 text-center rounded-lg bg-blue-50">
              <div className="text-2xl font-bold text-blue-700">{progress.pending}</div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
            <div className="p-4 text-center rounded-lg bg-red-50">
              <div className="text-2xl font-bold text-red-700">{progress.failed}</div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>

          {/* Failures Section */}
          {progress.failed > 0 && (
            <div className="pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowFailures(!showFailures)}
                className="flex items-center justify-between w-full text-left"
              >
                <h2 className="text-lg font-semibold text-gray-800">
                  ⚠️ {progress.failed} Job{progress.failed > 1 ? 's' : ''} Failed
                </h2>
                <span className="text-gray-600">{showFailures ? '▼' : '▶'}</span>
              </button>

              {showFailures && failures.length > 0 && (
                <div className="mt-4 space-y-2">
                  {failures.map((failure) => (
                    <div
                      key={failure.job_id}
                      className="p-3 border border-red-200 rounded-lg bg-red-50"
                    >
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Clinician ID:</span>{' '}
                        {failure.clinician_id}
                      </div>
                      <div className="mt-1 text-sm text-red-700">
                        <span className="font-medium">Error:</span> {failure.error}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Failed at: {new Date(failure.failed_at * 1000).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Completion Message */}
          {progress.finalized && (
            <div className="p-4 mt-6 border border-green-200 rounded-lg bg-green-50">
              <p className="font-medium text-green-800">✅ Payroll run completed successfully!</p>
              <p className="mt-1 text-sm text-green-700">
                CSV reports have been sent to the configured recipients.
              </p>
              {payPeriodPath && (
                <a
                  href={payPeriodPath}
                  className="inline-block mt-3 text-sm text-green-700 underline hover:text-green-800"
                >
                  View Pay Period Details →
                </a>
              )}
            </div>
          )}
        </>
      )}
    </>
  )
}

// Form Component
interface DryRunResult {
  validation_passed: boolean
  errors: string[]
  new_start_date: string
  new_end_date: string
  bonus_period: boolean
  calculated_bonus_period: boolean
  payable_clinicians_count: number
  recipient_email: string
  previous_pay_period: {
    id: number
    start_date: string
    end_date: string
    bonus_payout_period: boolean
  }
}

function PayrollForm({
  previousPayPeriod,
  nextPayPeriod,
  calculatedBonusPeriod,
  payableClinicianCount,
  currentUserEmail,
  payrollPath,
  previousPayPeriodPath,
}: {
  previousPayPeriod: PayrollProps['previousPayPeriod']
  nextPayPeriod: PayrollProps['nextPayPeriod']
  calculatedBonusPeriod: boolean
  payableClinicianCount: number
  currentUserEmail: string
  payrollPath: string
  previousPayPeriodPath?: string
}) {
  const [bonusPeriod, setBonusPeriod] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [errors, setErrors] = React.useState<string[]>([])
  const [dryRunResult, setDryRunResult] = React.useState<DryRunResult | null>(null)
  const [showDryRun, setShowDryRun] = React.useState(false)
  const [isRollingBack, setIsRollingBack] = React.useState(false)

  // Calculate if rollback is allowed (within 48 hours of creation)
  const canRollback = React.useMemo(() => {
    if (!previousPayPeriod?.created_at) return false

    const createdAt = new Date(previousPayPeriod.created_at)
    const now = new Date()
    const hoursSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60)

    return hoursSinceCreation <= 48
  }, [previousPayPeriod])

  // Calculate if payroll is blocked (less than 24 hours since previous pay period ended)
  const cannotRunPayroll = React.useMemo(() => {
    if (!previousPayPeriod?.end_date) return true

    const endDate = new Date(previousPayPeriod.end_date)
    // Add 24 hours to the end date (which is midnight of that day)
    const minimumRunDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000)
    const now = new Date()

    return now < minimumRunDate
  }, [previousPayPeriod])

  // Calculate time remaining until payroll can be run
  const timeUntilCanRun = React.useMemo(() => {
    if (!previousPayPeriod?.end_date || !cannotRunPayroll) return null

    const endDate = new Date(previousPayPeriod.end_date)
    const minimumRunDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000)
    const now = new Date()
    const timeRemaining = minimumRunDate.getTime() - now.getTime()

    const hours = Math.floor(timeRemaining / (1000 * 60 * 60))
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60))

    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  }, [previousPayPeriod, cannotRunPayroll])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setErrors([])

    try {
      const response = await fetch(payrollPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
        body: JSON.stringify({
          bonus_payout_period: bonusPeriod,
        }),
      })

      const data = await response.json()

      if (data.success) {
        // Reload page to show progress view
        window.location.href = `${payrollPath}?pay_period_id=${data.pay_period_id}`
      } else {
        setErrors(data.errors || ['An error occurred'])
        setIsSubmitting(false)
      }
    } catch {
      setErrors(['Failed to start payroll run'])
      setIsSubmitting(false)
    }
  }

  const handleRollback = async () => {
    if (!previousPayPeriod?.id) return

    const confirmMessage = `Are you sure you want to rollback the pay period from ${previousPayPeriod.start_date} to ${previousPayPeriod.end_date}? This will delete all pay stubs and the pay period itself.`

    if (!window.confirm(confirmMessage)) return

    setIsRollingBack(true)
    setErrors([])

    try {
      const response = await fetch(`/pay_periods/${previousPayPeriod.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': getCSRFToken(),
        },
      })

      if (response.ok) {
        // Redirect to payroll page
        window.location.href = payrollPath
      } else {
        const errorText = await response.text()
        setErrors([`Failed to rollback pay period: ${errorText}`])
        setIsRollingBack(false)
      }
    } catch (error) {
      setErrors([`Failed to rollback pay period: ${error}`])
      setIsRollingBack(false)
    }
  }

  const handleDryRun = async () => {
    setErrors([])
    setShowDryRun(false)

    try {
      const response = await fetch(`${payrollPath}/dry_run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
        body: JSON.stringify({
          bonus_payout_period: bonusPeriod,
        }),
      })

      if (!response.ok) {
        // Try to parse error response
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        let debugInfo = null

        try {
          const errorData = await response.json()
          console.error('Dry run error response:', errorData)

          if (errorData.error) {
            errorMessage = errorData.error
          } else if (errorData.errors) {
            errorMessage = Array.isArray(errorData.errors)
              ? errorData.errors.join(', ')
              : JSON.stringify(errorData.errors)
          } else if (errorData.message) {
            errorMessage = errorData.message
          }

          // Capture debug info if available
          if (errorData.debug_info) {
            debugInfo = errorData.debug_info
            console.error('Debug info:', debugInfo)

            // Add backtrace to error message if available
            if (debugInfo.backtrace && Array.isArray(debugInfo.backtrace)) {
              errorMessage += `\n\nStack trace:\n${debugInfo.backtrace.slice(0, 3).join('\n')}`
            }
          }
        } catch (parseError) {
          console.error('Error parsing response:', parseError)
          // If response isn't JSON, try to get text
          try {
            const errorText = await response.text()
            if (errorText) {
              console.error('Error response text:', errorText)
              errorMessage = `${errorMessage} - ${errorText.substring(0, 500)}`
            }
          } catch {
            // Use default error message
          }
        }

        setErrors([`Dry run failed: ${errorMessage}`])
        return
      }

      const data: DryRunResult = await response.json()
      setDryRunResult(data)
      setShowDryRun(true)

      if (!data.validation_passed) {
        setErrors(data.errors || [])
      }
    } catch (error) {
      console.error('Dry run error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      setErrors([`Failed to perform dry run: ${errorMessage}`])
    }
  }

  return (
    <>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Start Payroll Run</h1>

      {/* Pay Period Info */}
      <div className="p-4 mb-6 rounded-lg bg-gray-50">
        <div className="flex justify-between items-start mb-3">
          <h2 className="text-lg font-semibold text-gray-800">Pay Period Information</h2>
          <div className="flex gap-2">
            {previousPayPeriodPath && (
              <a
                href={previousPayPeriodPath}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                View previous pay periods results →
              </a>
            )}
            {canRollback && previousPayPeriod?.id && (
              <button
                type="button"
                onClick={handleRollback}
                disabled={isRollingBack}
                className="px-3 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRollingBack ? 'Rolling back...' : 'Rollback Pay'}
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-600">Previous Period:</span>
            <div className="text-gray-900">
              {previousPayPeriod?.start_date} to {previousPayPeriod?.end_date}
            </div>
          </div>
          <div>
            <span className="font-medium text-gray-600">New Period:</span>
            <div className="text-gray-900">
              {nextPayPeriod?.start_date} to {nextPayPeriod?.end_date}
            </div>
          </div>
          <div>
            <span className="font-medium text-gray-600">Payable Clinicians:</span>
            <div className="text-gray-900">{payableClinicianCount}</div>
          </div>
          <div>
            <span className="font-medium text-gray-600">Mission Bonus Payout Period:</span>
            <div className="text-gray-900">{calculatedBonusPeriod ? 'Yes' : 'No'}</div>
          </div>
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="p-4 mb-6 border border-red-200 rounded-lg bg-red-50">
          <h3 className="mb-2 text-sm font-semibold text-red-800">Validation Errors:</h3>
          <ul className="text-sm text-red-700 list-disc list-inside">
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Bonus Period Checkbox */}
        <div className="mb-6">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={bonusPeriod}
              onChange={(e) => setBonusPeriod(e.target.checked)}
              className="w-5 h-5 mr-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-base font-medium text-gray-900">
              Pay Mission Bonus this period
            </span>
          </label>
          {calculatedBonusPeriod !== bonusPeriod && (
            <p className="mt-2 text-sm text-amber-600">
              ⚠️ This differs from the system calculation ({calculatedBonusPeriod ? 'Yes' : 'No'})
            </p>
          )}
        </div>

        {/* Email Recipient Display */}
        <div className="mb-6">
          <div className="block mb-2 text-base font-medium text-gray-900">
            Payroll Report Recipient
          </div>
          <div className="p-3 border border-gray-200 rounded-md bg-gray-50">
            <span className="text-gray-700">{currentUserEmail}</span>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            The payroll report will be sent to your email address.
          </p>
        </div>

        {/* 24-hour guard warning */}
        {cannotRunPayroll && timeUntilCanRun && (
          <div className="p-4 mb-6 border rounded-lg border-amber-200 bg-amber-50">
            <h3 className="mb-2 text-sm font-semibold text-amber-800">
              ⚠️ Payroll Cannot Be Run Yet
            </h3>
            <p className="text-sm text-amber-700">
              Payroll can only be run at least 24 hours after the previous pay period ends.
            </p>
            <p className="mt-1 text-sm text-amber-700">
              The previous pay period ended on {previousPayPeriod?.end_date}.
            </p>
            <p className="mt-1 text-sm font-medium text-amber-800">
              You can run payroll in {timeUntilCanRun}.
            </p>
          </div>
        )}

        {/* Dry Run Results */}
        {showDryRun && dryRunResult && (
          <div className="p-4 mb-6 border border-gray-200 rounded-lg bg-gray-50">
            <h3 className="mb-3 text-lg font-semibold text-gray-800">Dry Run Results</h3>

            <div className="space-y-3">
              {/* Validation Status */}
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">Validation:</span>
                {dryRunResult.validation_passed ? (
                  <span className="font-medium text-green-700">✅ Passed</span>
                ) : (
                  <span className="font-medium text-red-700">❌ Failed</span>
                )}
              </div>

              {/* Pay Period Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Previous Period:</span>
                  <div className="text-gray-900">
                    {dryRunResult.previous_pay_period?.start_date} to{' '}
                    {dryRunResult.previous_pay_period?.end_date}
                    {dryRunResult.previous_pay_period?.bonus_payout_period && ' (Bonus)'}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-gray-600">New Period:</span>
                  <div className="text-gray-900">
                    {dryRunResult.new_start_date} to {dryRunResult.new_end_date}
                  </div>
                </div>
              </div>

              {/* Bonus Period Comparison */}
              <div className="text-sm">
                <span className="font-medium text-gray-600">Mission Bonus Payout Period:</span>
                <div className="text-gray-900">
                  Selected: {dryRunResult.bonus_period ? 'Yes' : 'No'} | Calculated:{' '}
                  {dryRunResult.calculated_bonus_period ? 'Yes' : 'No'}
                  {dryRunResult.bonus_period !== dryRunResult.calculated_bonus_period && (
                    <span className="ml-2 text-amber-600">⚠️ Differs from calculation</span>
                  )}
                </div>
              </div>

              {/* Clinician Count */}
              <div className="text-sm">
                <span className="font-medium text-gray-600">Payable Clinicians:</span>
                <span className="ml-2 text-gray-900">{dryRunResult.payable_clinicians_count}</span>
              </div>

              {/* Errors if any */}
              {dryRunResult.errors && dryRunResult.errors.length > 0 && (
                <div className="p-3 mt-3 border border-red-200 rounded bg-red-50">
                  <span className="text-sm font-medium text-red-800">Validation Errors:</span>
                  <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                    {dryRunResult.errors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleDryRun}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Dry Run
          </button>
          <button
            type="submit"
            disabled={isSubmitting || errors.length > 0 || cannotRunPayroll}
            className="px-6 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              cannotRunPayroll
                ? `Payroll cannot be run until 24 hours after the previous pay period ends`
                : undefined
            }
          >
            {isSubmitting ? 'Starting...' : 'Start Payroll Run'}
          </button>
        </div>
      </form>
    </>
  )
}

// Main Component
export default function Payroll(props: PayrollProps) {
  return (
    <div className="max-w-4xl p-6 mx-auto">
      <div className="p-6 bg-white rounded-lg shadow-md">
        {props.mode === 'progress' && props.payPeriodId ? (
          <PayrollProgress
            payrollProgressPath={props.payrollProgressPath || ''}
            payrollFailuresPath={props.payrollFailuresPath || ''}
            payPeriodPath={props.payPeriodPath}
          />
        ) : (
          <PayrollForm
            previousPayPeriod={props.previousPayPeriod}
            nextPayPeriod={props.nextPayPeriod}
            calculatedBonusPeriod={props.calculatedBonusPeriod || false}
            payableClinicianCount={props.payableClinicianCount || 0}
            currentUserEmail={props.currentUserEmail || ''}
            payrollPath={props.payrollPath}
            previousPayPeriodPath={props.previousPayPeriodPath}
          />
        )}
      </div>
    </div>
  )
}
